// Lint exception: camera calibration values
import type { MapControls } from "@react-three/drei";
import type { ElementRef } from "react";
import { MathUtils, MOUSE, Spherical, Vector3 } from "three";
import type { PerspectiveCamera as PerspectiveCameraImpl } from "three";

import { CAMERA_VIEW, VIEW_DEFAULTS } from "./camera.constants.js";
import type {
  CameraAngleRange,
  CameraBounds,
  CameraDistanceLimits,
  CameraTargetBounds,
  CameraVector,
  CameraViewConfig,
  CameraViewPlacement,
  CameraViewport,
} from "./camera.types.js";

function clampPolarAngleRange(range: CameraAngleRange) {
  const min = MathUtils.clamp(
    Math.min(range.min, range.max),
    CAMERA_VIEW.minPolarAngle,
    CAMERA_VIEW.maxPolarAngle
  );
  const max = MathUtils.clamp(
    Math.max(range.min, range.max),
    min,
    CAMERA_VIEW.maxPolarAngle
  );

  return { max, min };
}

function normalizeAzimuthAngle(angle: number) {
  return MathUtils.euclideanModulo(angle + Math.PI, Math.PI * 2) - Math.PI;
}

function normalizeAzimuthAngleRange(range: CameraAngleRange) {
  const min = normalizeAzimuthAngle(range.min);
  const max = normalizeAzimuthAngle(range.max);

  if (min <= max) {
    return { max, min };
  }

  return { max: min, min: max };
}

function normalizeTargetBounds(
  bounds: CameraTargetBounds,
  target: CameraVector
): CameraTargetBounds {
  const [minX, minY, minZ] = bounds.min;
  const [maxX, maxY, maxZ] = bounds.max;
  const [targetX, targetY, targetZ] = target;

  return {
    max: [
      Math.max(minX, maxX, targetX),
      Math.max(minY, maxY, targetY),
      Math.max(minZ, maxZ, targetZ),
    ],
    min: [
      Math.min(minX, maxX, targetX),
      Math.min(minY, maxY, targetY),
      Math.min(minZ, maxZ, targetZ),
    ],
  };
}

function normalizeCameraViewConfig(viewConfig: CameraViewConfig) {
  return {
    ...viewConfig,
    azimuthAngle: normalizeAzimuthAngleRange(viewConfig.azimuthAngle),
    fitPaddingFactor: Math.max(viewConfig.fitPaddingFactor, 1),
    maxDistanceFactor: MathUtils.clamp(
      Math.max(viewConfig.minDistanceFactor, viewConfig.maxDistanceFactor),
      CAMERA_VIEW.absoluteMinDistanceFactor,
      CAMERA_VIEW.absoluteMaxDistanceFactor
    ),
    minDistanceFactor: MathUtils.clamp(
      Math.min(viewConfig.minDistanceFactor, viewConfig.maxDistanceFactor),
      CAMERA_VIEW.absoluteMinDistanceFactor,
      CAMERA_VIEW.absoluteMaxDistanceFactor
    ),
    polarAngle: clampPolarAngleRange(viewConfig.polarAngle),
  };
}

export function getCameraViewConfig(topDown: boolean): CameraViewConfig {
  const defaults = topDown ? VIEW_DEFAULTS.mobile : VIEW_DEFAULTS.desktop;

  return normalizeCameraViewConfig(defaults);
}

function getViewDirection(viewConfig: CameraViewConfig) {
  const polarAngle =
    (viewConfig.polarAngle.min + viewConfig.polarAngle.max) / 2;
  const azimuthAngle =
    (viewConfig.azimuthAngle.min + viewConfig.azimuthAngle.max) / 2;

  return new Vector3().setFromSpherical(
    new Spherical(1, polarAngle, azimuthAngle)
  );
}

function getBoundsCorners(bounds: CameraBounds) {
  const [minX, minY, minZ] = bounds.min;
  const [maxX, maxY, maxZ] = bounds.max;

  return [
    new Vector3(minX, minY, minZ),
    new Vector3(minX, minY, maxZ),
    new Vector3(minX, maxY, minZ),
    new Vector3(minX, maxY, maxZ),
    new Vector3(maxX, minY, minZ),
    new Vector3(maxX, minY, maxZ),
    new Vector3(maxX, maxY, minZ),
    new Vector3(maxX, maxY, maxZ),
  ];
}

function getCameraBasis(viewDirection: Vector3) {
  const fallbackUp =
    Math.abs(viewDirection.y) > 0.99
      ? new Vector3(0, 0, 1)
      : new Vector3(0, 1, 0);
  const right = new Vector3()
    .crossVectors(fallbackUp, viewDirection)
    .normalize();
  const up = new Vector3().crossVectors(viewDirection, right).normalize();

  return { right, up };
}

export function getCameraDistanceLimits({
  defaultDistance,
  viewConfig,
}: {
  defaultDistance: number;
  viewConfig: CameraViewConfig;
}): CameraDistanceLimits {
  return {
    maxDistance: MathUtils.clamp(
      defaultDistance * viewConfig.maxDistanceFactor,
      CAMERA_VIEW.absoluteMinDistance,
      CAMERA_VIEW.absoluteMaxDistance
    ),
    minDistance: MathUtils.clamp(
      defaultDistance * viewConfig.minDistanceFactor,
      CAMERA_VIEW.absoluteMinDistance,
      CAMERA_VIEW.absoluteMaxDistance
    ),
  };
}

export function hasPannableTargetBounds(targetBounds: CameraTargetBounds) {
  const [minX, minY, minZ] = targetBounds.min;
  const [maxX, maxY, maxZ] = targetBounds.max;

  return minX !== maxX || minY !== maxY || minZ !== maxZ;
}

function getDefaultTargetBounds({
  officeBounds,
  target,
}: {
  officeBounds: CameraBounds;
  target: CameraVector;
}): CameraTargetBounds {
  const [, targetY] = target;
  const [minX, , minZ] = officeBounds.min;
  const [maxX, , maxZ] = officeBounds.max;

  return {
    max: [maxX, targetY, maxZ],
    min: [minX, targetY, minZ],
  };
}

export function getDefaultCameraPlacement({
  officeBounds,
  viewport,
  viewConfig,
}: {
  officeBounds: CameraBounds;
  viewport: CameraViewport;
  viewConfig: CameraViewConfig;
}): CameraViewPlacement {
  const target = officeBounds.center;
  const targetVector = new Vector3(...target);
  const viewDirection = getViewDirection(viewConfig);
  const { right, up } = getCameraBasis(viewDirection);
  const verticalHalfFov = MathUtils.degToRad(CAMERA_VIEW.fov) / 2;
  const horizontalHalfFov = Math.atan(
    Math.tan(verticalHalfFov) * viewport.aspect
  );
  const tanVerticalHalfFov = Math.tan(verticalHalfFov);
  const tanHorizontalHalfFov = Math.tan(horizontalHalfFov);

  let fitDistance: number = CAMERA_VIEW.absoluteMinDistance;
  let maxForwardOffset = Number.NEGATIVE_INFINITY;

  for (const corner of getBoundsCorners(officeBounds)) {
    const relativeCorner = corner.clone().sub(targetVector);
    const horizontalOffset = Math.abs(relativeCorner.dot(right));
    const verticalOffset = Math.abs(relativeCorner.dot(up));
    const forwardOffset = relativeCorner.dot(viewDirection);
    const horizontalDistance =
      forwardOffset + horizontalOffset / tanHorizontalHalfFov;
    const verticalDistance =
      forwardOffset + verticalOffset / tanVerticalHalfFov;

    fitDistance = Math.max(fitDistance, horizontalDistance, verticalDistance);
    maxForwardOffset = Math.max(maxForwardOffset, forwardOffset);
  }

  const defaultDistance = MathUtils.clamp(
    Math.max(fitDistance, maxForwardOffset + CAMERA_VIEW.near) *
      viewConfig.fitPaddingFactor,
    CAMERA_VIEW.absoluteMinDistance,
    CAMERA_VIEW.absoluteMaxDistance
  );
  const position = viewDirection
    .clone()
    .multiplyScalar(defaultDistance)
    .add(targetVector);

  return {
    defaultDistance,
    position: [position.x, position.y, position.z],
    target,
    targetBounds: normalizeTargetBounds(
      getDefaultTargetBounds({
        officeBounds,
        target,
      }),
      target
    ),
  };
}

export function getEffectiveTargetBounds({
  officeBounds,
  placement,
}: {
  officeBounds: CameraBounds | null;
  placement: CameraViewPlacement | null;
}): CameraTargetBounds {
  if (placement) {
    return placement.targetBounds;
  }

  const target: CameraVector = officeBounds?.center ?? [0, 0, 0];

  return {
    max: target,
    min: target,
  };
}

export function configureOrbitControls({
  controls,
  viewConfig,
  distanceLimits,
  canPan,
}: {
  controls: ElementRef<typeof MapControls>;
  viewConfig: CameraViewConfig;
  distanceLimits: CameraDistanceLimits | null;
  canPan: boolean;
}) {
  controls.autoRotate = false;
  controls.enableDamping = false;
  controls.dampingFactor = 0;
  controls.enablePan = canPan;
  controls.minAzimuthAngle = viewConfig.azimuthAngle.min;
  controls.maxAzimuthAngle = viewConfig.azimuthAngle.max;
  controls.minPolarAngle = viewConfig.polarAngle.min;
  controls.maxPolarAngle = viewConfig.polarAngle.max;
  controls.panSpeed = 1.5;
  controls.screenSpacePanning = false;
  controls.mouseButtons = {
    LEFT: MOUSE.PAN,
    MIDDLE: MOUSE.DOLLY,
    RIGHT: MOUSE.ROTATE,
  };

  if (distanceLimits) {
    controls.minDistance = distanceLimits.minDistance;
    controls.maxDistance = distanceLimits.maxDistance;
  }
}

export function applyCameraView({
  camera,
  controls,
  canPan,
  distanceLimits,
  placement,
  viewConfig,
}: {
  camera: PerspectiveCameraImpl;
  controls: ElementRef<typeof MapControls>;
  canPan: boolean;
  distanceLimits: CameraDistanceLimits;
  placement: CameraViewPlacement;
  viewConfig: CameraViewConfig;
}) {
  camera.zoom = 1;
  camera.updateProjectionMatrix();

  const target = new Vector3(...placement.target);
  const position = new Vector3(...placement.position);

  camera.position.copy(position);
  camera.lookAt(target);
  camera.updateProjectionMatrix();
  controls.target.copy(target);

  configureOrbitControls({
    canPan,
    controls,
    distanceLimits,
    viewConfig,
  });

  controls.update();
}

export function clampOrbitControlsTarget({
  camera,
  controls,
  targetBounds,
}: {
  camera: PerspectiveCameraImpl;
  controls: ElementRef<typeof MapControls>;
  targetBounds: CameraTargetBounds;
}) {
  const [minX, minY, minZ] = targetBounds.min;
  const [maxX, maxY, maxZ] = targetBounds.max;
  const clampedTarget = controls.target
    .clone()
    .set(
      MathUtils.clamp(controls.target.x, minX, maxX),
      MathUtils.clamp(controls.target.y, minY, maxY),
      MathUtils.clamp(controls.target.z, minZ, maxZ)
    );

  const delta = clampedTarget.sub(controls.target);

  if (delta.lengthSq() === 0) {
    return;
  }

  controls.target.add(delta);
  camera.position.add(delta);
  controls.update();
}
